import { useMutation } from "@tanstack/react-query";
import { createColumnHelper } from "@tanstack/react-table";
import { useCallback, useMemo, useState } from "react";
import {
  Alert,
  ButtonGroup,
  ButtonToolbar,
  Col,
  Dropdown,
  Row,
} from "react-bootstrap";
import { Link } from "react-router-dom";
import type { BillingReport } from "../../../api/src/billing/lib";
import Loading from "../Loading";
import ActionButton from "../components/ActionButton";
import HelpButton from "../components/HelpButton";
import TabContainerWithTabs from "../components/TabContainer";
import { Table, metaDefault, useTable } from "../components/Table/Table";
import {
  YesNoSingleFilter,
  multiSelectIncludes,
  yesNoOptions,
} from "../components/Table/filters";
import {
  useAuthenticatedFileFetch,
  useAuthenticatedMutation,
} from "../lib/api";
import { formatCurrency, formatPercent } from "../lib/numbers";
import BillingNav from "./BillingNav";
import {
  deserializeBillingReport,
  hasFigures,
  reportStateLabel,
} from "./BillingReportInfo";
import FinalizeConfirmation from "./FinalizeConfirmation";
import { boolToYesNo } from "../lib/display";
import {
  BillingHelp,
  DELETE_BILLING_REPORT_MUTATION_KEY,
  useQueryBillingReports,
  useQueryExportBillingReport,
} from "./lib";

type BillingReportRow = BillingReport;

const columnHelper = createColumnHelper<BillingReportRow>();

function getBillingPeriodAnnum(billingReport: BillingReport) {
  const yearInSeconds = 31556952;
  return (
    1 /
    ((billingReport.endDate.valueOf() - billingReport.startDate.valueOf()) /
      yearInSeconds /
      1000)
  );
}

const BillingReportsTable = () => {
  const { isPending, isError, data } = useQueryBillingReports();

  const billingReports: BillingReport[] = useMemo(
    () => (data?.data ?? []).map(deserializeBillingReport),
    [data?.data],
  );

  /**
   * The report whose Finalize control was pressed, and so the one the
   * confirmation is about; undefined while no confirmation is up.
   */
  const [finalizeReportId, setFinalizeReportId] = useState<number>();

  const hideFinalizeConfirmation = useCallback(
    () => setFinalizeReportId(undefined),
    [],
  );

  const columns = useMemo(
    () => [
      columnHelper.accessor("endDate", {
        cell: (info) => (
          <Link to={`/billing/reports/${info.row.original.id}`}>
            {info.getValue().toLocaleDateString()}
          </Link>
        ),
        header: () => "Billing Date",
      }),
      columnHelper.accessor("createdTime", {
        cell: (info) => info.getValue().toLocaleDateString(),
        header: () => "Created",
      }),
      columnHelper.accessor("status", {
        id: "reportState",
        cell: (info) => reportStateLabel(info.getValue()),
        header: () => "State",
        enableColumnFilter: false,
      }),
      columnHelper.accessor(
        (row) => {
          const firmWide = yesNoOptions.find(
            (a) => a.label === (row.isFirmWideExecution ? "Yes" : "No"),
          );
          return firmWide?.value.toString() ?? "";
        },
        {
          id: "isFirmWideExecution",
          cell: (info) => {
            return info.getValue() ? "Yes" : "No";
          },
          filterFn: multiSelectIncludes,
          header: () => "Firm-Wide?",
          meta: {
            filterComponent: YesNoSingleFilter,
          },
        },
      ),
      columnHelper.accessor("billableAUM", {
        cell: (info) =>
          !hasFigures(info.row.original.status) ? (
            "Pending..."
          ) : (
            <div>
              {formatCurrency(info.getValue())}
              <br />
              <span className="text-muted">
                AUM {formatCurrency(info.row.original.totalAUM)}
              </span>
            </div>
          ),
        header: () => "Billable Balance",
        enableColumnFilter: false,
        meta: {
          className: "text-end",
          headerClassName: "text-end",
        },
      }),
      // On 0 to 100 on the wire (`billingReportPercents`,
      // `api/src/billing/billing.controller.ts`); `formatPercent` takes 0 to 1.
      columnHelper.accessor("effectiveFeeRate", {
        cell: (info) =>
          !hasFigures(info.row.original.status)
            ? null
            : formatPercent(info.getValue() / 100, 2),
        header: () => "Effective Fee Rate",
        enableColumnFilter: false,
      }),
      columnHelper.accessor("totalFee", {
        cell: (info) =>
          !hasFigures(info.row.original.status) ? null : (
            <div>
              {formatCurrency(info.getValue())}
              <br />
              <span className="text-muted">
                {formatCurrency(
                  info.getValue() * getBillingPeriodAnnum(info.row.original),
                )}{" "}
                p.a.
              </span>
            </div>
          ),
        header: () => "Debited",
        enableColumnFilter: false,
        meta: {
          className: "text-end",
          headerClassName: "text-end",
        },
      }),
      columnHelper.accessor("totalAssetAdjustmentDollarAmount", {
        cell: (info) =>
          !hasFigures(info.row.original.status)
            ? null
            : formatCurrency(info.getValue()),
        header: () => "Total Exclusion",
        enableColumnFilter: false,
        meta: {
          className: "text-end",
          headerClassName: "text-end",
        },
      }),
      columnHelper.accessor("totalFeeSplit", {
        cell: (info) =>
          !hasFigures(info.row.original.status) ? null : (
            <div>
              {formatCurrency(info.getValue())}
              <br />
              <span className="text-muted">
                {formatCurrency(
                  info.getValue() * getBillingPeriodAnnum(info.row.original),
                )}{" "}
                p.a.
              </span>
            </div>
          ),
        header: () => "Total Split",
        enableColumnFilter: false,
        meta: {
          className: "text-end",
          headerClassName: "text-end",
        },
      }),
      columnHelper.accessor(
        (row) => {
          const invoiceCreated = yesNoOptions.find(
            (a) =>
              a.label ===
              (row.latestInvoiceDownloadTime !== null ? "Yes" : "No"),
          );
          return invoiceCreated?.value.toString() ?? "";
        },
        {
          id: "latestInvoiceDownloadTime",
          header: () => "Invoices Created?",
          cell: (info) => {
            return !hasFigures(info.row.original.status)
              ? null
              : boolToYesNo(info.getValue() === "true");
          },
          filterFn: multiSelectIncludes,
          meta: {
            filterComponent: YesNoSingleFilter,
          },
        },
      ),
      columnHelper.display({
        id: "actions",
        cell: (info) => {
          const report = info.row.original;

          // eslint-disable-next-line react-hooks/rules-of-hooks
          const { refetch: downloadInvoices } = useAuthenticatedFileFetch(
            `/billing/reports/${report.id}/invoices`,
            { method: "POST" },
            {
              enabled: false,
            },
          );

          // eslint-disable-next-line react-hooks/rules-of-hooks
          const downloadInvoicesMutation = useMutation({
            mutationFn: async () => {
              await downloadInvoices();
            },
          });

          // eslint-disable-next-line react-hooks/rules-of-hooks
          const { refetch: downloadFeeUpload } = useAuthenticatedFileFetch(
            `/billing/reports/${report.id}/fee-upload`,
            { method: "POST" },
            {
              enabled: false,
            },
          );

          // eslint-disable-next-line react-hooks/rules-of-hooks
          const downloadFeeUploadMutation = useMutation({
            mutationFn: async () => {
              await downloadFeeUpload();
            },
          });

          const { isLoading: isLoadingExport, refetch: refetchExport } =
            // eslint-disable-next-line react-hooks/rules-of-hooks
            useQueryExportBillingReport(report.id);

          // eslint-disable-next-line react-hooks/rules-of-hooks
          const onExport = useCallback(async () => {
            await refetchExport();
          }, [refetchExport]);

          // eslint-disable-next-line react-hooks/rules-of-hooks
          const deleteReport = useAuthenticatedMutation(
            `/v1/billing/reports/${report.id}`,
            { method: "DELETE" },
          );

          // `useQueryBillingReports` walks the list again when this succeeds
          // (`DELETE_BILLING_REPORT_MUTATION_KEY`).
          // eslint-disable-next-line react-hooks/rules-of-hooks
          const deleteReportMutation = useMutation({
            mutationKey: DELETE_BILLING_REPORT_MUTATION_KEY,
            mutationFn: async () => {
              await deleteReport();
            },
          });

          return (
            <Dropdown>
              <Dropdown.Toggle />
              <Dropdown.Menu
                popperConfig={{
                  modifiers: [
                    {
                      name: "flip",
                      options: {
                        fallbackPlacements: ["bottom", "top"],
                      },
                    },
                  ],
                }}
              >
                <Dropdown.Header>Actions</Dropdown.Header>
                {!hasFigures(report.status) ? null : (
                  <>
                    <Dropdown.Item
                      onClick={() => downloadInvoicesMutation.mutate()}
                    >
                      Download Invoices
                    </Dropdown.Item>
                    <Dropdown.Item
                      onClick={() => downloadFeeUploadMutation.mutate()}
                    >
                      Download Fee Upload
                    </Dropdown.Item>
                    <Dropdown.Item
                      onClick={onExport}
                      disabled={isLoadingExport}
                    >
                      Export
                    </Dropdown.Item>
                  </>
                )}
                {/*
                  Only a draft can be finalized: `complete` is the sole status
                  the transition starts from (R1). A finalized report shows
                  that in the State column instead — there is no way back out
                  of `finalized` for it to offer (R7).
                */}
                {report.status !== "complete" ? null : (
                  <Dropdown.Item onClick={() => setFinalizeReportId(report.id)}>
                    Finalize
                  </Dropdown.Item>
                )}
                {/*
                  R6: a finalized report offers no delete control. This is
                  defence in depth only — the service refuses the delete (R5),
                  and must keep refusing it however this renders.

                  The other statuses are deliberately untouched: Delete has
                  always rendered for `pending` and `failed` reports, and
                  changing that is not this change.
                */}
                {report.status === "finalized" ? null : (
                  <Dropdown.Item onClick={() => deleteReportMutation.mutate()}>
                    Delete
                  </Dropdown.Item>
                )}
              </Dropdown.Menu>
            </Dropdown>
          );
        },
      }),
    ],
    [],
  );

  const { table } = useTable({
    columns,
    data: billingReports,
    getRowId: (row) => row.id.toString(),
    autoResetPageIndex: true,
    meta: metaDefault,
    initialState: {
      sorting: [{ id: "endDate", desc: true }],
    },
  });

  return (
    <TabContainerWithTabs tabs={BillingNav}>
      <Row>
        <Col className="d-flex justify-content-between mb-3">
          <ButtonToolbar>
            <ButtonGroup className="me-3">
              <ActionButton
                variant="secondary"
                label="Run Firm-Wide Billing"
                icon="/icons/new.svg"
                className="btn-action_secondary_wide"
                as={Link}
                to="/billing/reports/run?type=standard"
              />
            </ButtonGroup>
            <ButtonGroup className="me-3">
              <ActionButton
                variant="secondary"
                label="Run One-Off Billing"
                icon="/icons/new.svg"
                className="btn-action_secondary_wide"
                as={Link}
                to="/billing/reports/run?type=custom"
              />
            </ButtonGroup>
          </ButtonToolbar>
          <HelpButton
            title="Help – Billing"
            body={<BillingHelp />}
            buttonProps={{ className: "mb-3" }}
          />
        </Col>
      </Row>
      <Row>
        <Col>
          {isPending ? (
            <Loading />
          ) : isError ? (
            <Alert variant="danger">An error occurred</Alert>
          ) : !billingReports || billingReports.length <= 0 ? (
            <Alert>No billing reports found</Alert>
          ) : (
            <Table table={table} />
          )}
        </Col>
      </Row>
      {/*
        The same confirmation the report page renders, so the advisor reads the
        same words and the same finalize request is sent whichever surface they
        pressed Finalize on (R26). `useQueryBillingReports` walks the list again
        once the finalize succeeds, so the report's State cell and its Finalize
        and Delete items all reflect the new status. The workflow's steps run on
        after the request returns (R30); nothing here waits for them.
      */}
      {typeof finalizeReportId === "undefined" ? null : (
        <FinalizeConfirmation
          show
          reportId={finalizeReportId}
          onHide={hideFinalizeConfirmation}
        />
      )}
    </TabContainerWithTabs>
  );
};

export default BillingReportsTable;
